import * as XLSX from 'xlsx';
import { xlsxWorkbookToCsv } from '@erp/shared-utils/src/xlsx-to-csv';

/** Extensions accepted by the data-asset file pickers. */
export const DATA_ASSET_ACCEPT =
  '.json,.csv,.tsv,.ndjson,.jsonl,.txt,.xml,.xlsx,.xls';

const XLSX_EXTENSION_RE = /\.(xlsx|xls|xlsm|xlsb)$/i;

export interface PreparedUpload {
  blob: Blob;
  fileName: string;
  contentType: string;
}

/**
 * Turn a picked file into the bytes we actually store.
 *
 * Spreadsheets are converted to CSV in the browser with the same converter the
 * backend uses, so every stored data asset is text a workflow step can read
 * directly. Everything else is uploaded verbatim.
 */
export async function prepareUploadFile(file: File): Promise<PreparedUpload> {
  if (!XLSX_EXTENSION_RE.test(file.name)) {
    return {
      blob: file,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    };
  }

  return spreadsheetToCsvUpload(await file.arrayBuffer(), file.name);
}

/** Convert workbook bytes to the CSV blob we store, renaming `.xlsx` → `.csv`. */
export function spreadsheetToCsvUpload(
  bytes: ArrayBuffer,
  fileName: string,
): PreparedUpload {
  const workbook = XLSX.read(bytes, { type: 'array' });
  const csv = xlsxWorkbookToCsv(workbook);

  return {
    blob: new Blob([csv], { type: 'text/csv' }),
    fileName: fileName.replace(XLSX_EXTENSION_RE, '.csv'),
    contentType: 'text/csv',
  };
}

/**
 * PUT a blob to a presigned storage URL.
 *
 * `x-ms-blob-type` is required by Azure and must NOT be sent to S3 — and the
 * response has to be checked, otherwise a rejected upload is confirmed as a
 * healthy version and the asset silently holds no content.
 */
export async function putToSignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': contentType };
  if (uploadUrl.includes('.blob.core.windows.net')) {
    headers['x-ms-blob-type'] = 'BlockBlob';
  }

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers,
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    console.error('Data asset upload failed:', {
      status: response.status,
      statusText: response.statusText,
      body,
      url: uploadUrl.split('?')[0],
    });
    throw new Error(
      `Failed to upload file: ${response.status} ${response.statusText}`,
    );
  }
}

/** Best-effort human-readable message for an upload failure. */
export function uploadErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Upload failed';
}
